/**
 * OSRM Match + Route service helper.
 *
 * Base URL is fleet-config driven (no hardcoded host). Resolution order:
 *   fleetSettings.osrmBaseUrl > fleetSettings.OsrmBaseUrl
 *   > process.env.REACT_APP_OSRM_BASE_URL > fallback https://osrm5.hbssweb.net
 *
 * Call configureOsrm({ baseUrl, fleetSettings }) once on app/LiveDispatch mount;
 * `isOsrmEnabled()` reflects whether matching is active. When the base URL is missing
 * or empty, helpers no-op and the meter falls back to raw Haversine.
 */

const DEFAULT_OSRM_BASE_URL = 'https://routing.tranware.net';
const DEFAULT_RADIUS_M = 25;
const MATCH_TIMEOUT_MS = 8000;
const ROUTE_TIMEOUT_MS = 6000;
const NEAREST_TIMEOUT_MS = 4000;

let _baseUrl = '';
let _enabled = false;
let _loggedStartup = false;

function trimSlash(s) {
  return typeof s === 'string' ? s.replace(/\/+$/, '') : '';
}

function resolveFromFleetSettings(fleetSettings) {
  if (!fleetSettings || typeof fleetSettings !== 'object') return '';
  return (
    fleetSettings.osrmBaseUrl ||
    fleetSettings.OsrmBaseUrl ||
    fleetSettings.OSRMBaseUrl ||
    fleetSettings.osrmUrl ||
    ''
  );
}

/**
 * Configure the OSRM base URL once. Idempotent — safe to call repeatedly with the same value.
 *
 * @param {object} [opts]
 * @param {string} [opts.baseUrl]        Explicit override (highest priority).
 * @param {object} [opts.fleetSettings]  Fleet settings object (osrmBaseUrl/OsrmBaseUrl).
 */
export function configureOsrm(opts = {}) {
  const fromFleet = trimSlash(resolveFromFleetSettings(opts.fleetSettings));
  const fromEnv = trimSlash(
    typeof process !== 'undefined' && process.env ? process.env.REACT_APP_OSRM_BASE_URL : ''
  );
  const explicit = trimSlash(opts.baseUrl || '');
  const resolved = explicit || fromFleet || fromEnv || trimSlash(DEFAULT_OSRM_BASE_URL);

  _baseUrl = resolved || '';
  _enabled = !!_baseUrl;

  if (!_loggedStartup) {
    if (_enabled) {
      // eslint-disable-next-line no-console
      console.log('OSRM Match: enabled, base =', _baseUrl);
    } else {
      // eslint-disable-next-line no-console
      console.log('OSRM Match: disabled (no base URL configured) — falling back to raw Haversine');
    }
    _loggedStartup = true;
  }
  return _enabled;
}

export function isOsrmEnabled() {
  return _enabled && !!_baseUrl;
}

export function getOsrmBaseUrl() {
  return _baseUrl;
}

/** Build the OSRM Match URL for a sequence of `{lat,lng,ts,accuracy?}` points. */
export function getMatchUrl(points, opts = {}) {
  if (!_enabled || !_baseUrl) return '';
  if (!Array.isArray(points) || points.length < 2) return '';
  const coords = points.map((p) => `${p.lng},${p.lat}`).join(';');
  const radiuses = points
    .map((p) => Math.max(8, Math.min(60, Number(p.accuracy) || DEFAULT_RADIUS_M)))
    .join(';');
  const timestamps = points.map((p) => Math.floor((Number(p.ts) || Date.now()) / 1000)).join(';');
  const params = new URLSearchParams({
    timestamps,
    radiuses,
    geometries: 'geojson',
    overview: opts.overview || 'full',
    tidy: 'true',
    annotations: 'distance',
  });
  return `${_baseUrl}/match/v1/driving/${coords}?${params.toString()}`;
}

/**
 * Sanitize a trace window for OSRM Match:
 *   - drop consecutive points whose displacement is below a small threshold (degenerate
 *     traces of repeated fixes return 400 NoMatch)
 *   - enforce strictly-increasing 1-second-resolution timestamps (OSRM rejects ties)
 * Returns a new array; the original is not mutated.
 */
function sanitizeMatchWindow(points) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const MIN_SEPARATION_M = 3; // below GPS jitter floor
  const out = [];
  let prev = null;
  for (const p of points) {
    if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue;
    if (prev) {
      // Cheap equirectangular displacement (sub-50 m accuracy is plenty for jitter dedup).
      const dLat = (p.lat - prev.lat) * 111_320;
      const dLng = (p.lng - prev.lng) * 111_320 * Math.cos((prev.lat * Math.PI) / 180);
      const distM = Math.sqrt(dLat * dLat + dLng * dLng);
      if (distM < MIN_SEPARATION_M) continue;
    }
    out.push({ ...p });
    prev = p;
  }
  // Enforce strictly-increasing 1-second-resolution timestamps so the URL builder doesn't
  // emit identical `timestamps=` entries (OSRM treats those as zero-Δt and refuses to match).
  let lastTs = -Infinity;
  for (const p of out) {
    let ts = Math.floor((Number(p.ts) || Date.now()) / 1000);
    if (ts <= lastTs) ts = lastTs + 1;
    p.ts = ts * 1000;
    lastTs = ts;
  }
  return out;
}

/** Build the OSRM Route URL between two `{lat,lng}` points. */
export function getRouteUrl(a, b) {
  if (!_enabled || !_baseUrl) return '';
  if (!a || !b) return '';
  return `${_baseUrl}/route/v1/driving/${a.lng},${a.lat};${b.lng},${b.lat}?overview=false`;
}

/** Build the OSRM Nearest (snap-to-road) URL for a single `{lat,lng}` point. */
export function getNearestUrl(a, opts = {}) {
  if (!_enabled || !_baseUrl) return '';
  if (!a || !Number.isFinite(a.lat) || !Number.isFinite(a.lng)) return '';
  const number = Math.max(1, Math.min(5, Number(opts.number) || 1));
  return `${_baseUrl}/nearest/v1/driving/${a.lng},${a.lat}?number=${number}`;
}

async function fetchWithTimeout(url, timeoutMs) {
  if (typeof fetch !== 'function') {
    throw new Error('fetch is not available in this environment');
  }
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => {
        try {
          controller.abort();
        } catch (e) {
          /* ignore */
        }
      }, timeoutMs)
    : null;
  try {
    const res = await fetch(url, controller ? { signal: controller.signal } : undefined);
    if (!res.ok) throw new Error(`OSRM HTTP ${res.status}`);
    return await res.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Run OSRM Match against a buffered window of GPS fixes.
 * Returns `{ matchedDistanceMeters, hasGap, raw }` or `null` on failure / disabled.
 */
export async function matchTraceWindow(points) {
  if (!isOsrmEnabled()) return null;
  if (!Array.isArray(points) || points.length < 2) return null;

  // Drop degenerate / repeated fixes — OSRM Match returns 400 NoMatch when every point is
  // within GPS noise of its neighbor, or when timestamps repeat (zero-Δt segments).
  const cleaned = sanitizeMatchWindow(points);
  if (cleaned.length < 2) {
    // Not enough movement to warrant a Match call; raw odometer remains authoritative.
    return null;
  }

  const url = getMatchUrl(cleaned);
  if (!url) return null;

  let json;
  try {
    json = await fetchWithTimeout(url, MATCH_TIMEOUT_MS);
  } catch (err) {
    return null;
  }

  if (!json || json.code !== 'Ok' || !Array.isArray(json.matchings) || json.matchings.length === 0) {
    return null;
  }

  let matchedDistanceMeters = 0;
  for (const m of json.matchings) {
    if (Number.isFinite(m?.distance)) matchedDistanceMeters += Number(m.distance);
  }

  // OSRM Match may split the trace when a long gap is detected; > 1 matching = gap.
  const hasGap = json.matchings.length > 1;

  return { matchedDistanceMeters, hasGap, raw: json };
}

/** OSRM Route between two coords. Returns `{ distanceMeters, durationSeconds }` or `null`.
 *  `durationSeconds` is OSRM's expected free-flow travel time at posted speeds — used by the
 *  meter to split a background gap into driving vs waiting portions. */
export async function routeBetween(a, b) {
  if (!isOsrmEnabled()) return null;
  const url = getRouteUrl(a, b);
  if (!url) return null;

  let json;
  try {
    json = await fetchWithTimeout(url, ROUTE_TIMEOUT_MS);
  } catch (err) {
    return null;
  }

  if (!json || json.code !== 'Ok' || !Array.isArray(json.routes) || json.routes.length === 0) {
    return null;
  }
  const distanceMeters = Number(json.routes[0].distance) || 0;
  const durationSeconds = Number(json.routes[0].duration) || 0;
  return { distanceMeters, durationSeconds };
}

/**
 * OSRM Nearest — snap a single GPS fix to the nearest road segment.
 * Returns `{ lat, lng, distanceMeters, name }` (distanceMeters = how far the raw fix
 * was from the snapped point) or `null` on failure / disabled. Useful for cleaning up
 * jittery fixes before they enter the soft-meter odometer.
 */
export async function nearestSnap(a, opts = {}) {
  if (!isOsrmEnabled()) return null;
  const url = getNearestUrl(a, opts);
  if (!url) return null;

  let json;
  try {
    json = await fetchWithTimeout(url, NEAREST_TIMEOUT_MS);
  } catch (err) {
    return null;
  }

  if (!json || json.code !== 'Ok' || !Array.isArray(json.waypoints) || json.waypoints.length === 0) {
    return null;
  }
  const wp = json.waypoints[0];
  const loc = Array.isArray(wp.location) ? wp.location : null; // [lng, lat]
  if (!loc || !Number.isFinite(loc[0]) || !Number.isFinite(loc[1])) return null;
  return {
    lat: loc[1],
    lng: loc[0],
    distanceMeters: Number.isFinite(wp.distance) ? Number(wp.distance) : 0,
    name: typeof wp.name === 'string' ? wp.name : '',
  };
}

// Self-configure on module load with env fallback so test setup works without an explicit call.
configureOsrm({});
